import React, { useState } from "react";
import { MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export const StorefrontLocationPicker = ({ shopId, vendorId, locations }) => {
  const { user } = useAuth();
  const { cart, setLocation } = useCart();
  const [pending, setPending] = useState(null);
  const selected = cart?.locations?.[vendorId]?.location_id;

  if (!locations || locations.length < 2) return null;

  const choose = async (loc, confirmClear = false) => {
    if (!user) { toast.message("Sign in to choose a location"); return; }
    if (loc.location_id === selected) return;
    try {
      await setLocation(shopId, loc.location_id, confirmClear);
      toast.success(`Ordering from ${loc.label}`);
      setPending(null);
    } catch (e) {
      if (e?.response?.status === 409) setPending(loc);
      else toast.error(e?.response?.data?.detail || "Couldn't switch location");
    }
  };

  return (
    <section id="storefront-location-picker" className="rounded-3xl bg-white/[0.03] border border-pink-400/30 p-5" data-testid="storefront-location-picker">
      <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
        <MapPin className="w-4 h-4 text-pink-300" /> Choose a location
      </h2>
      <p className="text-xs text-zinc-400 mt-1">Your bag is tied to one location for this shop.</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {locations.map((loc) => {
          const active = loc.location_id === selected;
          return (
            <button
              key={loc.location_id}
              onClick={() => choose(loc)}
              className={`text-left rounded-2xl border p-3 transition-colors ${active ? "border-pink-400 bg-pink-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/30"}`}
              data-testid={`storefront-location-${loc.location_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm">{loc.label}</span>
                {active && <Check className="w-4 h-4 text-pink-300" data-testid="storefront-location-selected" />}
              </div>
              {loc.address && <p className="text-xs text-zinc-400 mt-0.5">{loc.address}</p>}
              {loc.hours && <p className="text-[11px] text-zinc-500 mt-0.5">{loc.hours}</p>}
            </button>
          );
        })}
      </div>

      <AlertDialog open={Boolean(pending)} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="location-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to {pending?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Items from this shop already in your bag will be removed, since each order comes from one location.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="location-switch-cancel">Keep current</AlertDialogCancel>
            <AlertDialogAction onClick={() => choose(pending, true)} data-testid="location-switch-confirm">
              Switch & clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default StorefrontLocationPicker;
